import { Record, Map as ImmutableMap } from 'immutable';
import { stringify } from 'hub-http/helpers/params';
import { toMomentPortalTz } from 'foundations-components/transitional/utils/SimpleDate';
import { parseLocation } from 'automation-ui-routing/utils';
import * as AuditEventTypes from 'automation-ui-management/records/AuditHistory/AuditEventTypes';
import AuditEventHistoryFilterKeys, { AuditEventHistoryFrontendFilterKeys, AuditEventHistoryApiFilterKeys } from 'automation-ui-management/records/AuditHistory/AuditEventHistoryFilterKeys';
import { makeDateRangeFromParsedSearchString, toDateStringFromSimpleDate } from 'automation-ui-management/utils/dateRangeUtils';
import { ALL_PRESET_ID, CUSTOM_PRESET_ID } from 'automation-ui-management/constants/DateRangePickerOptions';
export const ALL_FILTER = 'all';
const AuditEventHistoryFilterDefaults = {
  [AuditEventHistoryFrontendFilterKeys.EVENT_TYPE]: AuditEventTypes.ALL_EVENT_TYPES,
  [AuditEventHistoryApiFilterKeys.USER_ID]: ALL_FILTER,
  [AuditEventHistoryApiFilterKeys.RANGE_TYPE]: ALL_PRESET_ID,
  [AuditEventHistoryApiFilterKeys.START_DATE]: null,
  [AuditEventHistoryApiFilterKeys.END_DATE]: null
};
export default class AuditEventHistoryFilters extends Record(AuditEventHistoryFilterDefaults, 'AuditEventHistoryFilters') {
  constructor(props) {
    const propMap = ImmutableMap(props || {});
    super(propMap);
  }
  updateFilter(filterKey, filterValue) {
    return this.set(filterKey, filterValue);
  }
  updateFilterFromDateRange({
    presetId,
    startDate,
    endDate
  }) {
    if (presetId === CUSTOM_PRESET_ID) {
      return this.merge({
        [AuditEventHistoryApiFilterKeys.RANGE_TYPE]: presetId,
        [AuditEventHistoryApiFilterKeys.START_DATE]: toDateStringFromSimpleDate(startDate),
        [AuditEventHistoryApiFilterKeys.END_DATE]: toDateStringFromSimpleDate(endDate)
      });
    }
    return this.merge({
      [AuditEventHistoryApiFilterKeys.RANGE_TYPE]: presetId,
      [AuditEventHistoryApiFilterKeys.START_DATE]: null,
      [AuditEventHistoryApiFilterKeys.END_DATE]: null
    });
  }
  updateFilterFromLocation(location) {
    const parsed = parseLocation(location);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let updatedFilter = this;
    Object.keys(parsed).forEach(key => {
      if (this.has(key)) {
        updatedFilter = updatedFilter.set(key, parsed[key]);
      }
    });
    return updatedFilter;
  }
  clearFilters() {
    return this.merge(AuditEventHistoryFilterDefaults);
  }
  isFilterApplied(filterKey) {
    return this[filterKey] !== AuditEventHistoryFilterDefaults[filterKey];
  }
  filtersAreApplied() {
    return Object.values(AuditEventHistoryFilterKeys).some(key => this.isFilterApplied(key));
  }
  getSearchFilters() {
    const searchFilters = Object.values(AuditEventHistoryFilterKeys).filter(key => this.isFilterApplied(key)).map(filterKey => `${filterKey}=${this[filterKey]}`).join('&');
    return searchFilters.length ? `?${searchFilters}` : '';
  }
  getFiltersForApi() {
    const userId = this.getUserIdFilter();
    const userIdFilterExistsAndIsApplied = this.isFilterApplied(AuditEventHistoryApiFilterKeys.USER_ID) && userId !== null;
    const dateRangeFilterApplied = this.isFilterApplied(AuditEventHistoryApiFilterKeys.RANGE_TYPE) && this.isFilterApplied(AuditEventHistoryApiFilterKeys.START_DATE) && this.isFilterApplied(AuditEventHistoryApiFilterKeys.END_DATE);
    const {
      cutoffTime,
      timestamp
    } = this.getDateRangeBounds();
    const filterQueryParams = {
      userId: userIdFilterExistsAndIsApplied ? userId : undefined,
      cutoffTime: dateRangeFilterApplied ? Number(cutoffTime) : undefined,
      timestamp: dateRangeFilterApplied ? Number(timestamp) : undefined
    };
    return filterQueryParams;
  }
  getStringifiedFiltersForApi() {
    return stringify(this.getFiltersForApi());
  }
  shouldFilterOut(auditEvent) {
    const eventTypeFilter = this.getEventTypeFilter();
    return this.isFilterApplied(AuditEventHistoryFrontendFilterKeys.EVENT_TYPE) && auditEvent.getAuditEventType() !== eventTypeFilter;
  }
  getDateRangeBounds() {
    const {
      endDate,
      startDate
    } = this.getDateRangeFilter();
    const timestamp = endDate ? toMomentPortalTz(endDate).add(1, 'days') : undefined;
    const cutoffTime = startDate ? toMomentPortalTz(startDate) : undefined;
    return {
      cutoffTime,
      timestamp
    };
  }
  getDateRangeFilter() {
    const rangeType = this.getRangeTypeFilter();
    const searchQuery = {
      rangeType: rangeType || undefined,
      startDate: this.getStartDateFilter(),
      endDate: this.getEndDateFilter()
    };
    const dateRangeFilter = makeDateRangeFromParsedSearchString(searchQuery);
    return dateRangeFilter;
  }

  // Filter getters
  getEventTypeFilter() {
    return this[AuditEventHistoryFrontendFilterKeys.EVENT_TYPE];
  }
  getUserIdFilter() {
    return this[AuditEventHistoryApiFilterKeys.USER_ID];
  }
  getRangeTypeFilter() {
    return this[AuditEventHistoryApiFilterKeys.RANGE_TYPE];
  }
  getStartDateFilter() {
    return this[AuditEventHistoryApiFilterKeys.START_DATE];
  }
  getEndDateFilter() {
    return this[AuditEventHistoryApiFilterKeys.END_DATE];
  }
}